import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Mail, Globe, ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t, language, toggleLanguage } = useLanguage();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success(language === 'ar' ? 'تم إرسال رابط إعادة التعيين إلى بريدك الإلكتروني' : 'Reset link sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || (language === 'ar' ? 'حدث خطأ' : 'An error occurred'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="max-w-md w-full">
        <div className="flex justify-between items-center mb-4">
          <Link to="/login" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-login">
            {language === 'ar' ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            {language === 'ar' ? 'تسجيل الدخول' : 'Login'}
          </Link>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-white rounded-lg border border-border"
            data-testid="language-toggle"
          >
            <Globe size={18} />
            {language === 'ar' ? 'English' : 'عربي'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-accent to-accent/70 rounded-xl flex items-center justify-center">
              <Building2 size={28} className="text-white" />
            </div>
            <span className="text-3xl font-heading font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{t('appName')}</span>
          </Link>
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">
            {language === 'ar' ? 'نسيت كلمة السر' : 'Forgot Password'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'ar' ? 'أدخل بريدك الإلكتروني وسنرسل لك رابط إعادة التعيين' : 'Enter your email and we\'ll send you a reset link'}
          </p>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-border shadow-lg">
          {sent ? (
            <div className="text-center py-6" data-testid="email-sent-success">
              <CheckCircle size={48} className="mx-auto mb-4 text-green-500" />
              <h2 className="text-xl font-heading font-bold text-primary mb-2">
                {language === 'ar' ? 'تم الإرسال!' : 'Email Sent!'}
              </h2>
              <p className="text-muted-foreground mb-4">
                {language === 'ar'
                  ? 'تم إرسال رابط إعادة تعيين كلمة السر إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد.'
                  : 'A password reset link has been sent to your email. Please check your inbox.'}
              </p>
              <p className="text-sm text-muted-foreground/70 mb-6">
                {language === 'ar'
                  ? 'الرابط صالح لمدة ساعة واحدة فقط'
                  : 'The link is valid for 1 hour only'}
              </p>
              <Link to="/login" data-testid="go-to-login-btn"
                className="inline-block px-6 py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg transition-all">
                {language === 'ar' ? 'العودة لتسجيل الدخول' : 'Back to Login'}
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                </label>
                <div className="relative">
                  <Mail size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email-input"
                    className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                    placeholder="example@company.com"
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="send-reset-link-btn"
                className="w-full py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
              >
                {loading
                  ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...')
                  : (language === 'ar' ? 'إرسال رابط إعادة التعيين' : 'Send Reset Link')}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
